import React, { useContext, useState } from 'react'
import styled from 'styled-components'
import SearchBar, { NoResults } from 'live/components/ui/SearchBar'
import i18n from 'live/services/i18n'
import { configsContext } from './AssetConfigs'
import { isProduct } from '../common/utils'
import { ProductAssetInfo } from './productAssetInfo'
import { FactoryAssetInfo } from './factoryAssetInfo'
import { orderBy } from 'lodash'
import { filterFactoryList, filterProductList } from './utils'
import { FactoryDialog } from './factoryDialog'
import { ProductDialog } from './productDialog'
import Scrollbars from 'react-scrollbars-custom'
import scrollbarStyles from '../common/scrollbarStyles'
interface IProps {
    assetTree: CountryData[] | ProductData[]
}

export function AssetList({ assetTree }: IProps): JSX.Element {
    const [showAddAssetModal, setShowAddAssetModal] = useState(false)
    const [filter, setFilter] = useState('')
    const { assetType } = useContext(configsContext)
    const isProductConfig = isProduct(assetType)
    function getSortedFactoriesArray(): FactoryWithCountryData[] {
        if (!assetTree) return
        const factories = (assetTree as CountryData[]).reduce((acc, country) => {
            country.factories?.map(factory => {
                const orderedLines = orderBy(factory.lines, [line => line.lineName.toLowerCase()])
                acc.push({ ...factory, lines: orderedLines, countryName: country.countryName })
            })

            return acc
        }, [])

        return orderBy(factories, [(factory: FactoryData) => factory.factoryName.toLowerCase()])
    }

    const sortedAndFilteredFactoryArray: FactoryWithCountryData[] = isProductConfig
        ? []
        : filterFactoryList(filter, getSortedFactoriesArray())

    const sortedAndFilteredProductsArray = isProductConfig ? filterProductList(filter, assetTree as ProductData[]) : []
    function showNoResults(): boolean {
        if (!filter) return false
        else if (isProductConfig && !sortedAndFilteredProductsArray.length) return true
        else if (!isProductConfig && !sortedAndFilteredFactoryArray.length) return true
        else return false
    }
    const factoriesOrProducts = isProductConfig ? 'Products' : 'Factories'

    return (
        <StyledAssetList className="assetList">
            <section className="assetList--topBar">
                <div className="assetList--topBar__container">
                    <button
                        className={'assetList--topBar__container--addAsset'}
                        onClick={() => setShowAddAssetModal(true)}
                    >
                        <i className="far fa-plus" />
                        {i18n('add')} {i18n(assetType)}
                    </button>
                    <StyledSearchBar filter={filter} setFilter={setFilter} />
                </div>
            </section>

            {!sortedAndFilteredProductsArray.length && !sortedAndFilteredFactoryArray.length ? (
                <div className="assetList--emptyMessage">
                    <p className="assetList--emptyMessage__icon">
                        <i className="far fa-empty-set"></i>
                    </p>
                    <p className="assetList--emptyMessage__header">{i18n(`No Registered ${factoriesOrProducts}`)}</p>
                    <p className="assetList--emptyMessage__message">
                        {i18n(
                            `To view the ${factoriesOrProducts.toLowerCase()}, it is necessary to register at least one.`
                        )}
                    </p>
                </div>
            ) : (
                <section className="assetList--content">
                    <h1 className="assetList--content__title">
                        {`${i18n('all')} ${i18n(isProductConfig ? 'products' : 'factories')}`}
                    </h1>
                    <table className="assetList--content__table">
                        <thead className="assetList--content__table--heading">
                            <tr className="assetList--content__table--heading__row">
                                <th className="assetList--content__table--heading__row--asset">{i18n(assetType)}</th>
                                <th className="assetList--content__table--heading__row--country">
                                    {i18n(isProductConfig ? 'id' : 'country')}
                                </th>
                                <th className="assetList--content__table--heading__row--lastEdited">
                                    {i18n('last edited')}
                                </th>
                                <th className="assetList--content__table--heading__row--author">{i18n('edited by')}</th>
                                <th className="assetList--content__table--heading__row--addLine"></th>
                                <th className="assetList--content__table--heading__row--editAsset"></th>
                                <th className="assetList--content__table--heading__row--removeAsset"></th>
                            </tr>
                        </thead>
                        <Scrollbars
                            thumbYProps={scrollbarStyles.thumbYProps}
                            trackYProps={{ style: { ...scrollbarStyles.trackYProps.style, right: -14 } }}
                            disableTrackYWidthCompensation
                            removeTracksWhenNotUsed
                            mobileNative
                            noScrollX
                        >
                            <tbody className="assetList--content__table--body">
                                {isProductConfig
                                    ? sortedAndFilteredProductsArray.map(product => (
                                          <ProductAssetInfo key={product.productId} productInfo={product} />
                                      ))
                                    : sortedAndFilteredFactoryArray.map(factory => {
                                          return (
                                              <FactoryAssetInfo
                                                  key={factory.factoryName}
                                                  factoryInfo={factory}
                                                  country={factory.countryName}
                                              />
                                          )
                                      })}
                            </tbody>
                        </Scrollbars>
                    </table>
                    <NoResults show={showNoResults()} searchLoading={false} clearFilters={() => setFilter('')} />
                </section>
            )}
            {assetType === 'product' ? (
                <ProductDialog showModal={showAddAssetModal} setShowModal={setShowAddAssetModal} isEdit={false} />
            ) : (
                <FactoryDialog showModal={showAddAssetModal} setShowModal={setShowAddAssetModal} isEdit={false} />
            )}
        </StyledAssetList>
    )
}

const StyledAssetList = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    .assetList {
        &--topBar__container,
        &--content {
            width: 970px;
            @media (min-width: 1600px) {
                width: 1170px;
            }
            @media (max-width: 1000px) {
                width: 100%;
            }
        }
        &--topBar {
            display: flex;
            align-items: center;
            justify-content: center;
            background: ${({ theme }) => theme['gray-200']};
            width: 100%;
            &__container {
                display: flex;
                padding: 7px 15px;

                &--addAsset {
                    border: none;
                    background: ${({ theme }) => theme['blue-500']};
                    color: ${({ theme }) => theme['gray-0']};
                    padding: 8px 24px;
                    font-size: 16px;
                    text-transform: capitalize;
                    display: flex;
                    align-items: center;
                    border-radius: 3px;
                    margin-right: 8px;
                    i {
                        margin-right: 5px;
                    }
                }
            }
        }
        &--content {
            padding: 0 35px;

            &__title {
                text-transform: uppercase;
                font-family: ${({ theme }) => theme['font-semibold']};
                font-size: 15px;
                line-height: 15px;
                margin: 33px 0 24px 15px;
            }
            &__table {
                width: 100%;
                height: calc(100vh - 250px);
                &--heading {
                    font-size: 13px;
                    color: ${({ theme }) => theme['gray-700']};
                    text-transform: capitalize;
                    &__row {
                        th {
                            font-weight: unset;
                            font-family: ${({ theme }) => theme['font-semibold']};
                            padding-bottom: 10px;
                            text-align: center;
                        }
                    }
                }
                &--body {
                    tr:last-child {
                        td {
                            border-bottom: 1px solid var(--gray-400);
                        }
                    }
                }
            }
        }
        &--emptyMessage {
            width: 250px;
            font-family: var(--font-semibold);
            margin-top: 15%;
            text-align: center;
            line-height: -25px;
            &__icon {
                color: var(--gray-500);
                font-size: 2rem;
                margin: 0;
            }
            &__header {
                color: var(--gray-800);
                margin: 0;
                font-weight: 900;
            }
            &__message {
                color: var(--gray-500);
                font-style: italic;
            }
        }
    }

    table {
        width: 100%;
        border-spacing: 0;
    }

    tbody {
        display: block;
    }

    tr {
        display: grid;
        grid-template-columns: 22% 22% 15% 15% 10% 8% 8%;
    }
    th,
    td {
        display: flex;
        justify-content: center;
        align-items: center;
        white-space: nowrap;
    }
    td :first-child {
        justify-content: flex-start;
    }
    td :first-child,
    td:nth-child(2) {
        white-space: break-spaces;
    }

    tbody {
        overflow-y: auto;
        overflow-x: hidden;
    }
    .ScrollbarsCustom-Scroller {
        padding-right: 1px;

        background: linear-gradient(white 30%, rgba(255, 255, 255, 0)),
            linear-gradient(rgba(255, 255, 255, 0), white 70%) 0 100%,
            radial-gradient(50% 0, farthest-side, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0)),
            radial-gradient(50% 100%, farthest-side, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0)) 0 100%;
        background: linear-gradient(white 30%, rgba(255, 255, 255, 0)),
            linear-gradient(rgba(255, 255, 255, 0), white 70%) 0 100%,
            radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0)),
            radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0)) 0 100%;
        background-repeat: no-repeat;
        background-color: white;
        background-size: 100% 40px, 100% 40px, 100% 14px, 100% 14px;
        background-attachment: local, local, scroll, scroll;
    }
`
export const StyledSearchBar = styled(SearchBar)`
    flex-grow: 1;
    box-shadow: none;
`
